import React, { useEffect, useMemo, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import type { Stripe } from '@stripe/stripe-js';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { SimpleSpinner } from '../components/SimpleSpinner';
import { alertManager } from '../services/AlertManager';
import { paymentApiService } from '../services/paymentApiService';
import { continuingEducationApiService } from '../services/continuingEducationApiService';
import {
    stripePromise,
    readPendingStripeReturn,
    clearPendingStripeReturn,
    type PendingStripeReturn,
} from '../utils/stripe';

const BILLING_PATH = '/settings/team/billing';
const CE_PATH = '/continuing-education';

type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface PaymentReturnResolution {
    path: string;
    alert?: { type: AlertType; message: string };
}

export interface PaymentReturnDeps {
    getStripe: () => Promise<Stripe | null>;
    finalizeTeamPayment: typeof paymentApiService.finalizePayment;
    finalizeCePurchase: typeof continuingEducationApiService.finalizePurchase;
}

const defaultDeps: PaymentReturnDeps = {
    getStripe: () => stripePromise,
    finalizeTeamPayment: (req) => paymentApiService.finalizePayment(req),
    finalizeCePurchase: (req) => continuingEducationApiService.finalizePurchase(req),
};

function billing(query: string, alert?: PaymentReturnResolution['alert']): PaymentReturnResolution {
    return { path: `${BILLING_PATH}?${query}`, alert };
}

/**
 * Works out where a Stripe redirect return lands and finishes the purchase on the way.
 * Never trusts redirect_status for money: team payments and CE purchases are finalized by the
 * server, which reads Stripe itself; otherwise Stripe.js is asked for the intent's status.
 */
export async function resolvePaymentReturn(
    params: URLSearchParams,
    pending: PendingStripeReturn | null,
    deps: PaymentReturnDeps = defaultDeps,
): Promise<PaymentReturnResolution> {
    const paymentIntentId = params.get('payment_intent');
    const paymentIntentSecret = params.get('payment_intent_client_secret');
    const setupIntentSecret = params.get('setup_intent_client_secret');
    const redirectStatus = params.get('redirect_status');

    if (paymentIntentId) {
        if (pending?.kind === 'team') {
            const res = await deps.finalizeTeamPayment({ teamId: pending.teamId, teamPaymentId: pending.teamPaymentId });
            switch (res.outcome) {
                case 'succeeded':
                    return billing('payment=succeeded', { type: 'success', message: res.message || 'Payment completed.' });
                case 'pending_settlement':
                    return billing('payment=pending', { type: 'info', message: res.message || 'Payment submitted.' });
                case 'incomplete':
                    // Money moved but was not applied; staff are notified. Not a failure to retry.
                    return billing('payment=pending', { type: 'warning', message: res.message || 'Your payment was received but could not be applied yet. Please do not pay again.' });
                default:
                    return billing('payment=failed', { type: 'error', message: res.message || 'Your bank did not confirm the payment.' });
            }
        }

        if (pending?.kind === 'continuingEducation') {
            const res = await deps.finalizeCePurchase({ paymentIntentId, paymentMethodType: pending.paymentMethodType });
            return res.success
                ? { path: `${CE_PATH}?payment=succeeded`, alert: { type: 'success', message: 'Purchase complete. You can watch the video now.' } }
                : { path: `${CE_PATH}?payment=failed`, alert: { type: 'error', message: res.message || 'Your bank did not confirm the payment.' } };
        }

        // Nothing stashed (storage blocked, new tab): report Stripe's status; webhooks apply it.
        let status: string | undefined;
        if (paymentIntentSecret) {
            const stripe = await deps.getStripe();
            const result = stripe ? await stripe.retrievePaymentIntent(paymentIntentSecret) : null;
            status = result?.paymentIntent?.status;
        }
        if (status === 'succeeded') return billing('payment=succeeded', { type: 'success', message: 'Payment completed.' });
        if (status === 'processing') return billing('payment=pending', { type: 'info', message: 'Payment submitted. It will appear once your bank confirms it.' });
        if (!status && redirectStatus === 'succeeded') return billing('payment=pending', { type: 'info', message: 'Payment submitted. It will appear once it is confirmed.' });
        return billing('payment=failed', { type: 'error', message: 'Your bank did not confirm the payment.' });
    }

    if (setupIntentSecret || params.get('setup_intent')) {
        let status: string | undefined;
        if (setupIntentSecret) {
            const stripe = await deps.getStripe();
            const result = stripe ? await stripe.retrieveSetupIntent(setupIntentSecret) : null;
            status = result?.setupIntent?.status;
        }
        const saved = status ? status === 'succeeded' || status === 'processing' : redirectStatus === 'succeeded';
        return saved
            ? billing('paymentMethod=saved', { type: 'success', message: 'Payment method saved.' })
            : billing('paymentMethod=failed', { type: 'error', message: 'Your bank did not confirm the payment method.' });
    }

    return { path: BILLING_PATH };
}

const PaymentReturnResolver: React.FC<{ pending: PendingStripeReturn | null }> = ({ pending }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        clearPendingStripeReturn();
        const params = new URLSearchParams(location.search);
        void (async () => {
            let resolution: PaymentReturnResolution;
            try {
                resolution = await resolvePaymentReturn(params, pending);
            } catch (err: unknown) {
                const failedPath = pending?.kind === 'continuingEducation' ? `${CE_PATH}?payment=failed` : `${BILLING_PATH}?payment=failed`;
                resolution = {
                    path: failedPath,
                    alert: { type: 'error', message: err instanceof Error ? err.message : 'Could not finish the payment.' },
                };
            }
            if (resolution.alert) alertManager.addAlert(resolution.alert.type, resolution.alert.message);
            navigate(resolution.path, { replace: true });
        })();
    }, [location.search, navigate, pending]);

    return <SimpleSpinner message="Finishing your payment…" />;
};

/**
 * /billing/payment-return: where Stripe sends the browser after a redirect-based bank
 * authentication. Signup has no login yet, so its marker forwards the Stripe params to the
 * signup payment page, which holds the signup session; everything else needs a signed-in user.
 */
export const PaymentReturnPage: React.FC = () => {
    const location = useLocation();
    const pending = useMemo(() => readPendingStripeReturn(), []);

    if (pending?.kind === 'signup') {
        return <Navigate to={`/auth/register/payment${location.search}`} replace />;
    }

    return (
        <ProtectedRoute requireAuth={true}>
            <PaymentReturnResolver pending={pending} />
        </ProtectedRoute>
    );
};

export default PaymentReturnPage;
